//文字コードはutf-8

/* カウントアップアニメーション
 * [data-count-up="<整数>"] が付いた要素を、ビューポートに入った瞬間に
 * 0（または現在表示値）から目標値まで補間する。
 *
 * - requestAnimationFrame で 1 フレームごとに値を更新
 * - 表示は Intl.NumberFormat('ja-JP') により 3 桁区切り（108,610,700）
 * - イージングは ease-out expo（1 - 2^(-10t)）
 *   t=0.3 で 87.5%、t=0.5 で 96.9% に到達。
 *   億単位の大桁では先頭「108」が早期に固定され、視線が離れにくい末尾の桁が
 *   ゆっくり進行することで「減速して着地する」感覚が知覚されやすくなる。
 * - data-count-up-duration="3000" で個別に長さ指定可（既定 2800ms）
 *   ※ 大桁ほど長めに。短すぎると先頭桁の遷移が読み取れず減速が見えない
 * - data-count-up-delay="400" で開始遅延（既定 0ms）
 * - data-count-up-bar="<セレクタ>" + data-count-up-goal="<目標値>" を併用すると、
 *   そのバー要素の CSS 変数 --count-up-progress を「現在値 / 目標値」で更新し、
 *   進捗バーが数値と完全に同期して伸びる（同一 RAF・同一イージング）。
 * - prefers-reduced-motion: reduce では即時に最終値
 *
 * 注: jQuery.animate({n: target}) でも同じことは可能だが、
 * 1) ステップ毎の数値フォーマットが扱いづらく、
 * 2) RAF 同期にならないため大桁の補間がカクつくため、ここでは RAF を使用。
========================================================*/

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const FORMATTER = (typeof Intl !== 'undefined' && Intl.NumberFormat)
    ? new Intl.NumberFormat('ja-JP')
    : { format: function (n) { return String(Math.round(n)); } };

const DEFAULT_DURATION = 2800;

// ease-out expo: 1 - 2^(-10t)
// 終端を 1.0 に揃えるため t === 1 の時のみ 1 を直接返す
function easeOutExpo(t) {
    return t >= 1 ? 1 : 1 - Math.pow(2, -10 * t);
}

// data-count-up-bar / data-count-up-goal から進捗バー設定を解決する。
// 未指定・要素なし・目標値が不正なら null（バー連動なし）。
function resolveBar($el) {
    const selector = $el.attr('data-count-up-bar');
    const goal = parseFloat($el.attr('data-count-up-goal'));
    if (!selector || isNaN(goal) || goal <= 0) return null;
    const el = document.querySelector(selector);
    return el ? { el: el, goal: goal } : null;
}

// 現在値からバーの幅（0〜100%）を求めて CSS 変数に反映する。
function setBarProgress(bar, value) {
    const pct = Math.max(0, Math.min(100, (value / bar.goal) * 100));
    bar.el.style.setProperty('--count-up-progress', pct + '%');
}

function animateNumber($el, from, to, duration, bar) {
    const start = performance.now();

    function step(now) {
        const elapsed = now - start;
        const t = Math.min(1, elapsed / duration);
        const value = from + (to - from) * easeOutExpo(t);
        $el.text(FORMATTER.format(Math.round(value)));
        if (bar) setBarProgress(bar, value);
        if (t < 1) {
            requestAnimationFrame(step);
        }
    }

    requestAnimationFrame(step);
}

export function initCountUp() {
    const $targets = $('[data-count-up]');
    if (!$targets.length) return;

    // モーション削減 or IO 非対応 → 即時最終値
    if (REDUCED_MOTION || typeof IntersectionObserver === 'undefined') {
        $targets.each(function () {
            const $el = $(this);
            const target = parseInt($el.attr('data-count-up'), 10);
            if (isNaN(target)) return;
            $el.text(FORMATTER.format(target));
            const bar = resolveBar($el);
            if (bar) setBarProgress(bar, target);
        });
        return;
    }

    // 注: 初期値はマークアップに書かれた値のまま（JS 無効時のフォールバック）。
    // ビューポート進入時に 0 へリセット → 目標値へ補間する。

    const observer = new IntersectionObserver(function (entries, obs) {
        entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            const $el = $(entry.target);
            const target = parseInt($el.attr('data-count-up'), 10);
            if (isNaN(target)) {
                obs.unobserve(entry.target);
                return;
            }
            const duration = parseInt($el.attr('data-count-up-duration') || DEFAULT_DURATION, 10);
            const delay = parseInt($el.attr('data-count-up-delay') || '0', 10);
            const bar = resolveBar($el);

            obs.unobserve(entry.target);

            // 進入直前に 0 へリセット → アニメーション開始（バーも 0% から）
            $el.text(FORMATTER.format(0));
            if (bar) setBarProgress(bar, 0);

            if (delay > 0) {
                setTimeout(function () {
                    animateNumber($el, 0, target, duration, bar);
                }, delay);
            } else {
                animateNumber($el, 0, target, duration, bar);
            }
        });
    }, {
        threshold: 0.4
    });

    $targets.each(function () {
        // 初期化時点でバーを 0% に空にしておく。
        // CSS フォールバック（≒60.845%）のままだとスクロール途中で
        // 既に伸びた状態が見え、進入時に 0% へ戻って再び伸びるため。
        const bar = resolveBar($(this));
        if (bar) setBarProgress(bar, 0);
        observer.observe(this);
    });
}
